/**
 * DESIGN.md §7: "One set. 20px in the App... 1.5px stroke, currentColor, round caps, no fills
 * mixed with strokes, inline SVG (no icon font)." This is a deliberately small starter set —
 * just what the foundation screens need. Extend this file rather than adding a second icon
 * mechanism.
 */
import type { SVGProps } from 'react';

export type IconName =
  | 'info'
  | 'check'
  | 'alert'
  | 'wifi-off'
  | 'log-out'
  | 'play'
  | 'pause'
  | 'search'
  | 'close'
  | 'chevron-down'
  | 'chevron-up'
  | 'chevron-left'
  | 'chevron-right'
  | 'grid'
  | 'list'
  | 'rows'
  | 'filter'
  | 'keyboard'
  | 'plus'
  | 'minus'
  | 'monitor'
  | 'sliders'
  | 'gear'
  | 'user'
  | 'volume-2'
  | 'volume-x'
  | 'announcement'
  | 'clock'
  | 'activity'
  | 'external-link'
  | 'skip-back'
  | 'skip-forward'
  | 'square'
  | 'inbox'
  | 'repeat'
  | 'rotate-ccw'
  | 'headphones'
  | 'wifi'
  | 'edit'
  | 'upload'
  | 'download'
  | 'mic-off'
  | 'mic'
  | 'theater'
  | 'maximize'
  | 'minimize'
  | 'eye'
  | 'eye-off'
  | 'heart';

const PATHS: Record<IconName, string> = {
  info: 'M12 16v-4M12 8h.01M22 12a10 10 0 1 1-20 0 10 10 0 0 1 20 0Z',
  check: 'M20 6 9 17l-5-5',
  alert: 'M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0ZM12 9v4M12 17h.01',
  'wifi-off': 'M1 1l22 22M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.58 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01',
  'log-out': 'M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9',
  play: 'M6 3l14 9-14 9V3Z',
  pause: 'M7 3h3v18H7zM14 3h3v18h-3z',
  search: 'M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16ZM21 21l-4.35-4.35',
  close: 'M18 6 6 18M6 6l12 12',
  'chevron-down': 'm6 9 6 6 6-6',
  'chevron-up': 'm18 15-6-6-6 6',
  'chevron-left': 'm15 18-6-6 6-6',
  'chevron-right': 'm9 18 6-6-6-6',
  grid: 'M4 4h7v7H4zM13 4h7v7h-7zM4 13h7v7H4zM13 13h7v7h-7z',
  list: 'M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01',
  rows: 'M3 5h18v4H3zM3 15h18v4H3z',
  filter: 'M4 6h16M7 12h10M11 18h2',
  keyboard: 'M3 6h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1ZM6 10h.01M10 10h.01M14 10h.01M18 10h.01M6 14h12',
  plus: 'M12 5v14M5 12h14',
  minus: 'M5 12h14',
  monitor: 'M3 4h18a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1ZM8 21h8M12 17v4',
  sliders: 'M4 6h16M4 12h10M4 18h7',
  // MVP-463: the Settings rail item used `sliders`, three stacked lines that read as a list or a
  // filter — which the library toolbar a few pixels away actually uses for filtering. A gear is the
  // one glyph nobody has to learn. The hub is drawn as two arcs rather than a <circle>, because this
  // map holds a single `d` per name.
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6ZM19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1Z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M8 7a4 4 0 1 0 8 0a4 4 0 1 0-8 0',
  'volume-2': 'M11 5 6 9H2v6h4l5 4V5ZM15.54 8.46a5 5 0 0 1 0 7.07M19.07 4.93a10 10 0 0 1 0 14.14',
  'volume-x': 'M11 5 6 9H2v6h4l5 4V5ZM23 9l-6 6M17 9l6 6',
  announcement: 'M12 20h.01M8.5 16.5a5 5 0 0 1 7 0M5 13a10 10 0 0 1 14 0',
  clock: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20ZM12 6v6l4 2',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  'external-link': 'M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14 21 3',
  'skip-back': 'M19 20 9 12l10-8v16ZM5 19V5',
  'skip-forward': 'M5 4l10 8-10 8V4ZM19 5v14',
  square: 'M3 3h18v18H3Z',
  inbox: 'M22 12h-6l-2 3h-4l-2-3H2M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11Z',
  repeat: 'M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3',
  'rotate-ccw': 'M1 4v6h6M3.51 15a9 9 0 1 0 2.13-9.36L1 10',
  headphones: 'M3 18v-6a9 9 0 0 1 18 0v6M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3ZM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3Z',
  wifi: 'M5 12.55a10.94 10.94 0 0 1 14 0M8.53 16.11a6 6 0 0 1 6.95 0M12 20h.01M1.42 9a15.91 15.91 0 0 1 21.16 0',
  edit: 'M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z',
  upload: 'M12 3v12M7 8l5-5 5 5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  // MVP-224: mirror of `upload` above (arrow pointing down into the tray instead of up out of it).
  download: 'M12 3v12M7 10l5 5 5-5M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2',
  'mic-off': 'M1 1l22 22M9 9v3a3 3 0 0 0 4.19 2.75M15 9.34V5a3 3 0 0 0-5.94-.6M17 16.95A7 7 0 0 1 5 12v-2M19 10v2a7 7 0 0 1-.11 1.23M12 19v4M8 23h8',
  // MVP-499: the un-struck form of `mic-off` above, for auto-duck's "listening" state. The pair has
  // to be the same drawing with and without the slash, exactly as `eye`/`eye-off` and `wifi`/
  // `wifi-off` already are — a different microphone for the on state would read as a different
  // control rather than the same one in its other state.
  mic: 'M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3ZM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8',
  // MVP-247: the cockpit video size controls. `theater` is the wide-mode toggle (a picture inset
  // in its frame, the same shape YouTube uses for it); `maximize`/`minimize` are the corner
  // brackets every player uses for enter/exit fullscreen.
  theater: 'M2 5h20v14H2zM6 9h12v6H6z',
  maximize: 'M8 3H5a2 2 0 0 0-2 2v3M16 3h3a2 2 0 0 1 2 2v3M8 21H5a2 2 0 0 1-2-2v-3M16 21h3a2 2 0 0 0 2-2v-3',
  minimize: 'M8 3v3a2 2 0 0 1-2 2H3M16 3v3a2 2 0 0 0 2 2h3M8 21v-3a2 2 0 0 0-2-2H3M16 21v-3a2 2 0 0 1 2-2h3',
  // MVP-293: the password reveal toggle. `eye-off` is the struck-through form the rest of this set
  // already uses for "this is off" (`wifi-off`, `mic-off`, `volume-x`) — same 1px-off-diagonal
  // slash, so the three read as one family rather than three drawings of the same idea.
  eye: 'M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8ZM15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z',
  'eye-off':
    'M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24M1 1l22 22',
  // MVP-412: the regular-singer toggle (SPEC AC-Q-5 — "explicitly the OpenKJ heart-icon
  // behaviour"). Stroke-only like the rest of the set: §7 bans mixing fills with strokes, so "on"
  // is carried by `IconButton`'s own `--active` treatment and `aria-pressed`, not by a second,
  // filled drawing of the same shape.
  heart: 'M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 1 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78Z',
};

export function Icon({ name, size = 20, ...rest }: { name: IconName; size?: number } & SVGProps<SVGSVGElement>) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      <path d={PATHS[name]} />
    </svg>
  );
}
